import type { ReactNode } from "react";

import type { ReviewRow } from "@/components/ui/transaction-review-contract";
import type { useExternalStepHandoff } from "@/features/finance/hooks/useExternalStepHandoff";
import type { TransactionRecord } from "@/lib/transaction-flow";

/** What every money path on the NFT page is given — the legacy flows' shared props, unchanged. */
export type SharedFlowProps = {
  lang: "fa" | "en";
  assetName: string;
  /** Passed straight through to the dock card. Optional — it falls back to an icon. */
  assetImage?: string;
  collectionName?: string | null;
  tokenId: string;
  networkLabel: string;
  royaltyPercent: number | null;
  contractAddress: string;
  onRecord: (entry: TransactionRecord) => void;
};

export type Handoff = ReturnType<typeof useExternalStepHandoff>;

/**
 * ONE MONEY PATH, as the dialog needs it — every prop the legacy
 * `TransactionFlowDialog` took, produced by the flow's hook from the legacy
 * component's own code. The v2 dialog only draws it.
 */
export type FlowModel = {
  lang: "fa" | "en";
  title: string;
  description?: string;
  rows: ReviewRow[];
  irreversibility: ReactNode;
  /** Visible label. The accessible name is this plus the irreversibility clause. */
  confirmLabel: string;
  confirmConsequence: string;
  confirmLoadingLabel?: string;
  confirmColor?: "primary" | "danger";
  /** Non-null blocks both continue and confirm, and states why beside the control. */
  blockedReason?: string | null;
  disableContinue?: boolean;
  /** False for a flow with nothing to enter — it opens straight at the review. */
  hasInput: boolean;
  /** The external-wallet round, when the backend asked for one. */
  handoff: Handoff;
  networkLabel: string;
  /** `null` means this attempt did not finish: nothing is recorded and the dialog stays on the review. */
  onConfirm: (requestId: string) => Promise<TransactionRecord | null>;
  closeOnSuccess?: boolean | (() => boolean);
  onRecord: (entry: TransactionRecord) => void;
  /** Already guarded: closing mid-signature is refused by the handoff. */
  onClose: () => void;
};

export function formatUnixDate(seconds: number | undefined | null, lang: "fa" | "en"): string | null {
  if (seconds === undefined || seconds === null || !Number.isFinite(Number(seconds))) return null;
  const date = new Date(Number(seconds) * 1000);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(lang === "fa" ? "fa-IR" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
